// Equipment attach / detach actions, extracted from GameBoard.jsx.
// Owns the equip flow (legal target computation, pending-equip
// selection, modal pick, commit) and detaching equipment back to the
// field, including the detach-all sweep when a host card leaves play.
// Equipped artifacts' continuous effects are applied/cleared by the
// staticAuras controller.

import { UI } from '../../../utils/arena/uiSounds';
import { buildSpawnInstance, FORCED_MOVE_OPS, getEffectiveStatuses, isForceMovable, TRIGGER } from '../../../utils/game/engine/automation';
import { countsAsUnit, hasSummoningSickness, isMonumentCard } from '../../../utils/game/engine/core';
import { countsAsMinion } from '../../../utils/game/engine/core/typeline';
import { effectiveMovementBonus } from '../../../utils/game/engine/core/movement';
import { fireDeferredFieldArtifactGenesis } from './automationRuntime';
import { lockedEquipmentIdSet } from './boardLegality';
import { toast } from 'sonner';
import { t } from '../../../localization/t';
import { cardLogLabel } from './logbookChat';
import { msg } from '../../../utils/game/logMessage';

/**
 * No type gating. Any card can be equipped to any other card. The
 * game has cards whose rules text bends the usual "auras + relics"
 * pattern (e.g. minions that can host other minions), and enforcing
 * a hard whitelist here would block those interactions. Players are
 * trusted to wire up the attachments their cards' rules describe.
 */
// ── Equipment ───────────────────────────────────────────────────
export function getValidEquipTargets(host, sourceCard) {
  // A Monument attaches only to the site on its own square; every other
  // source keeps the trust-the-player open target list.
  const sourceIsMonument = isMonumentCard(host._getFullCard(sourceCard.cardId));
  const targets = [];
  for (const [, mesh] of host.meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c || c.id === sourceCard.id) continue;
    if (!host.isCardOnLocalSide(c)) continue;
    if (sourceIsMonument && !isMonumentHostSite(host, sourceCard.id, c)) continue;
    targets.push({
      id: c.id,
      name: c.name,
      type: c.type,
      isSite: !!c.isSite,
      imageUrl: c.imageUrl,
      equippedCount: (c.equippedCards || []).length,
    });
  }
  targets.sort((a, b) => a.name.localeCompare(b.name));
  return targets;
}

// Walk pending cards and decide what kind of target the halos should
// highlight. If any pending card is a Monument-subtype artifact the
// user is trying to attach to a site; otherwise default to the
// carriable case (Minion / Avatar host).
export function resolveEquipTargetType(host) {
  for (const id of host.state.equipPending) {
    const mesh = host.meshes.get(id);
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    const fullCard = host._getFullCard(card.cardId);
    if (isMonumentCard(fullCard)) {
      return 'site';
    }
  }
  return 'unit';
}

// "col,row" key of the cell a card's mesh sits on, or null. A Monument
// can only attach to the site on its OWN square, so its valid Site
// target is filtered to the site sharing this cell.
function cellKeyOf(host, cardId) {
  const mesh = host.meshes.get(cardId);
  if (!mesh) return null;
  const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  return cell ? `${cell.col},${cell.row}` : null;
}

// True iff `card` is a Site occupying the same cell as the Monument at
// `monumentId`. The only Site a Monument may attach to.
function isMonumentHostSite(host, monumentId, card) {
  if (card.type !== 'Site') return false;
  const monumentCell = cellKeyOf(host, monumentId);
  return !!monumentCell && cellKeyOf(host, card.id) === monumentCell;
}

// Set of card ids that should pulse with a target halo right now.
// Sources: either an active equipPending (carriable / Monument
// attach flow) OR a pendingTarget (single-target effect waiting on
// click). Computed lazily so the overlay component only re-renders
// when membership churns.
export function getEquipTargetIds(host) {
  // Combat defend / intercept selection wins above everything: while the
  // player is choosing defenders/interceptors, halos render only around the
  // eligible units (teal), with chosen ones flipped to the bold gold ring
  // (chosenIds, passed separately to the overlay). Variant flips to teal.
  if (host._combatSelect && host._combatSelect.eligibleIds?.size) {
    return host._combatSelect.eligibleIds;
  }
  // Server-authoritative automation choice (P6 cutover): the server already
  // computed the legal candidates (public board ids); halo them directly.
  const psc = host.state.pendingServerChoice;
  if (psc && psc.candidates?.length) {
    return new Set(psc.candidates);
  }
  // Caster pick (v1.14) wins over everything else. Until the player
  // picks a Spellcaster the spell can't resolve, so halos are
  // restricted to valid casters.
  const casterPrompt = host.state.pendingPrompts?.find((p) => p.promptType === 'pick_caster');
  if (casterPrompt) {
    const casters = host._validSpellcastersFor(casterPrompt.controller);
    return new Set(casters.map((c) => c.id));
  }

  // Single-target effect mode wins next. It carries its own filter,
  // computed via the existing scope resolver. Caster overrides pivot
  // the source point to the picked spellcaster when set.
  const pt = host.state.pendingTarget;
  if (pt) {
    let matches = host._resolveAuraScope(
      pt.sourceCard,
      { where: pt.filter.scope || 'global' },
      { filter: pt.filter, exclude_self: pt.filter.exclude_self !== false },
      pt.casterOverrides || {},
    );
    // Forced-movement picks can't move `unmovable` units (Talamh Dreig).
    if (FORCED_MOVE_OPS.has(pt.op)) matches = matches.filter(isForceMovable);
    // Exclude the in-flight cast spell (Enchantress can't animate the
    // aura off its own cast. It isn't in the realm yet).
    if (pt.excludeId) matches = matches.filter((c) => c.id !== pt.excludeId);
    return new Set(matches.map((c) => c.id));
  }

  const pending = host.state.equipPending;
  if (!pending || pending.size === 0) return null;
  const targetType = host._resolveEquipTargetType();
  // For a Monument attach, the only legal Site is the one on the
  // Monument's own square (any pending Monument's cell qualifies).
  const monumentIds = targetType === 'site'
    ? [...pending].filter((id) => isMonumentCard(host._getFullCard(host.meshes.get(id)?.userData?.cardInstance?.cardId)))
    : [];
  const ids = new Set();
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (pending.has(card.id)) continue;
    if (!host.isCardOnLocalSide(card)) continue;
    if (targetType === 'site') {
      if (!monumentIds.some((mid) => isMonumentHostSite(host, mid, card))) continue;
    } else if (!countsAsUnit(card)) {
      continue;
    }
    ids.add(card.id);
  }
  return ids;
}

export function toggleEquipPending(host, cardInstance) {
  if (!cardInstance || !host.isCardOnLocalSide(cardInstance)) return;
  host.setState((state) => {
    const next = new Set(state.equipPending);
    if (next.has(cardInstance.id)) next.delete(cardInstance.id);
    else next.add(cardInstance.id);
    return { equipPending: next };
  });
}

export function clearEquipPending(host) {
  if (host.state.equipPending.size === 0) return;
  // Backing out of the equip pick leaves the artifact on the field.
  // a freshly played artifact still owing its deferred silent Genesis
  // (Shrine of the Dragonlord) fires it now, anchored on its own
  // cell. No-op for manual re-equip flows (no pending flag).
  for (const id of host.state.equipPending) {
    const card = host.meshes.get(id)?.userData?.cardInstance;
    if (card?._pendingFieldGenesis) fireDeferredFieldArtifactGenesis(host, card);
  }
  host.setState({ equipPending: new Set() });
}

/**
 * Take every card the user has Ctrl+clicked and attach each one to
 * `targetCard`. Skips the target itself if it happens to be in the
 * pending set so the user can't equip a card to itself.
 */
export function commitEquipPending(host, targetCard) {
  if (!targetCard || !host.isCardOnLocalSide(targetCard)) return false;
  const pending = host.state.equipPending;
  if (pending.size === 0) return false;

  let equippedAny = false;
  for (const sourceId of pending) {
    if (sourceId === targetCard.id) continue;
    const sourceMesh = host.meshes.get(sourceId);
    const sourceCard = sourceMesh?.userData?.cardInstance;
    if (!sourceCard) continue;
    host.equipCardTo(sourceCard, { id: targetCard.id });
    equippedAny = true;
  }
  host.setState({ equipPending: new Set() });
  return equippedAny;
}

export function openEquipModal(host, sourceCard) {
  const targets = host.getValidEquipTargets(sourceCard);
  host.setState({ equipModal: { source: sourceCard, targets }, contextMenu: null });
}

export function equipCardTo(host, sourceCard, targetSummary) {
  const targetMesh = host.meshes.get(targetSummary.id);
  const targetCard = targetMesh?.userData?.cardInstance;
  if (!targetCard) { host.closeContextMenu(); return; }
  if (!targetCard.equippedCards) targetCard.equippedCards = [];
  // Store the live cardInstance so a later detach (manual or
  // automatic when the host leaves the field) can re-spawn the card
  // with every piece of in-flight state. Statuses, ATK / DEF
  // modifiers, tapped / face-down flags. Fully intact.
  targetCard.equippedCards.push(sourceCard);
  host.removeCardFromTable(sourceCard, true, { detachEquipmentToField: false });
  host._broadcastCardEquipment(targetCard.id, targetCard.equippedCards);
  host.logAction('Equipped {card} to {target}', { card: cardLogLabel(sourceCard, msg('a card')), target: cardLogLabel(targetCard, msg('a card')) });
  // Equipping a Lance lights its status badge (badges read the
  // display-status union, which includes equipped Lances).
  host.rebuildStatusBadges(targetCard.id);
  host.syncHoverStatusOverlay();
  host.syncEquipmentOverlays();
  // Surface the level token on the bearer. An equipped counter card (The
  // Immortal Throne as a Relic) loses its own mesh, so the bearer is the
  // only place the count can show.
  host._refreshCounterHUD(targetCard.id);
  host.closeContextMenu();
  // Fire the equipped artifact's automation now that the equip is
  // committed. removeCardFromTable above won't have populated any
  // ledger entries for the artifact because applyCardAutomation
  // skips artifacts on play, so we're adding fresh entries here.
  host._applyEquippedArtifactEffects(sourceCard, targetCard);
  // on_pickup. The Relic just became carried. The bearer's controller
  // is the effective controller for the trigger (13 Treasures of
  // Britain: "bearer's controller draws a card"). Fires from this
  // single equip choke point so every carry path (modal pick, pending
  // ctrl-click, Lance toggle) lands here.
  host.applyCardAutomation(sourceCard, [TRIGGER.ON_PICKUP], {
    controller: targetCard.rotated ? 'p2' : 'p1',
  });
}

// The combat engine reads Lance from this same name predicate
// (_readCombatStats / _breakLanceOn). Equipment is the single source
// of truth for Lance, never a status flag.
const isLanceEquipment = (c) => (c?.name || '').toLowerCase() === 'lance';

export function hasLanceEquipped(cardInstance) {
  return (cardInstance?.equippedCards || []).some(isLanceEquipment);
}

// Status keys to DISPLAY for a card: the engine's effective statuses
// plus pseudo-statuses derived from elsewhere on the instance (Lance
// from equippedCards, summoning sickness from its flag). Neither is a
// real `statuses` entry, but the badge + hover-pill paths render them
// like any other status so they look consistent with Stealth /
// Voidwalk / etc. on the card. Engine rule logic still reads
// getEffectiveStatuses directly (no Lance, no sickness), so this is
// purely a presentation union.
export function displayStatusKeys(card) {
  const base = getEffectiveStatuses(card);
  const extra = [];
  if (hasLanceEquipped(card) && !base.includes('lance')) extra.push('lance');
  // hasSummoningSickness, not the raw flag: aura-granted Charge lifts the
  // sickness without clearing the stamp, and a badge for a minion that can
  // act would read as a bug.
  if (hasSummoningSickness(card) && !base.includes('summoning_sick')) extra.push('summoning_sick');
  return extra.length ? [...base, ...extra] : base;
}

// Stat chips the board draws on a card, as data for prompts that render a
// card thumbnail (BoardCardOverlays). Mirrors cardPileLifecycle.addCardToTable
// and refreshMovementHUD: minions carry ATK + DEF, plus the Movement chip
// while a bonus is in effect; avatars ATK only; everything else none.
// `delta` is live minus printed, the tint the in-canvas pill shows.
export function displayStatChips(card) {
  if (!card) return [];
  const atk = card.currentAttack ?? 0;
  const chips = [{ stat: 'atk', value: atk, delta: atk - (card.baseAttack ?? atk) }];
  if (countsAsMinion(card)) {
    const def = card.currentLife ?? 0;
    chips.push({ stat: 'def', value: def, delta: def - (card.baseLife ?? def) });
    const mov = effectiveMovementBonus(card);
    if (mov !== 0) chips.push({ stat: 'mov', value: mov, delta: mov });
    return chips;
  }
  return card.type === 'Avatar' ? chips : [];
}

/**
 * Status-ring "Lance" toggle. NOT a status: the entry is a shortcut
 * over the equipment system, because every Lance rule (codex: +1
 * damage vs units, strike first, breaks after striking a unit) keys
 * off `equippedCards` in the combat engine. Toggling ON equips a
 * fresh Lance artifact token; toggling OFF removes one. The ring's
 * lit state derives from the same array, so a Lance equipped any
 * other way (token browser + equip modal, picked up off the ground)
 * lights it too, and the auto-break after a fight un-lights it.
 */
export function toggleLanceEquip(host, hostCard) {
  if (!hostCard) return;
  const list = hostCard.equippedCards || [];
  const idx = list.findIndex(isLanceEquipment);
  if (idx !== -1) {
    const [lance] = hostCard.equippedCards.splice(idx, 1);
    host._clearEquippedArtifactEffects(lance.id);
    host._broadcastCardEquipment(hostCard.id, hostCard.equippedCards);
    host.logAction('{card} loses its Lance', { card: cardLogLabel(hostCard, msg('Unit')) });
  } else {
    const row = (host.props.sorceryCards || []).find((c) => (c.name || '').toLowerCase() === 'lance');
    const instance = row && buildSpawnInstance(row, {
      kind: 'token',
      rotated: !!hostCard.rotated,
      x: 0,
      z: 0,
    });
    if (!instance) {
      toast.error(t('Lance token not found in the card catalog'));
      return;
    }
    if (!hostCard.equippedCards) hostCard.equippedCards = [];
    hostCard.equippedCards.push(instance);
    host._broadcastCardEquipment(hostCard.id, hostCard.equippedCards);
    host.logAction('Equipped Lance to {card}', { card: cardLogLabel(hostCard, msg('a unit')) });
    host._applyEquippedArtifactEffects(instance, hostCard);
  }
  host.rebuildStatusBadges(hostCard.id);
  host.syncHoverStatusOverlay();
  host.syncEquipmentOverlays();
}

/**
 * Auto-equip a Lance token to a unit entering the realm with the Lance
 * keyword. Codex: "A minion with the Lance keyword enters the realm
 * carrying a Lance artifact token. Since the Lance keyword is an
 * 'enters the realm' effect, even if the minion is immediately disabled
 * or silenced when entering the realm, they will still have their Lance."
 * Idempotent. Skips if a Lance is already equipped (a fresh entry has
 * none; a bounce + replay re-grants). Combat reads the Lance off
 * `equippedCards`; `displayStatusKeys` surfaces the Lance status pill.
 * Returns true when a Lance was actually added (caller rebuilds badges).
 */
export function grantLanceOnEntry(host, hostCard) {
  if (!hostCard || !countsAsUnit(hostCard)) return false;
  // One grant per PRINTED Lance (the keyword list carries duplicates for
  // lance only; Thankless Squire prints three and each adds +1 in combat).
  const printedLances = (hostCard._printedKeywords || []).filter((k) => k === 'lance').length;
  if (printedLances === 0) return false;
  if (hasLanceEquipped(hostCard)) return false;
  const row = (host.props.sorceryCards || []).find((c) => (c.name || '').toLowerCase() === 'lance');
  if (!row) return false;
  if (!hostCard.equippedCards) hostCard.equippedCards = [];
  for (let i = 0; i < printedLances; i += 1) {
    const instance = buildSpawnInstance(row, { kind: 'token', rotated: !!hostCard.rotated, x: 0, z: 0 });
    if (!instance) return false;
    hostCard.equippedCards.push(instance);
    host._applyEquippedArtifactEffects(instance, hostCard);
  }
  const bearer = cardLogLabel(hostCard, msg('A unit'));
  if (printedLances > 1) host.logAction('{card} enters with {n} Lances', { card: bearer, n: printedLances });
  else host.logAction('{card} enters with a Lance', { card: bearer });
  return true;
}

/**
 * Detach a single piece of equipment, dropping it back on the field
 * at (or just beside) the host's current position. Driven by the
 * "Detach" button in the inspector's equipment panel.
 */
export function detachEquipment(host, hostCard, equippedId) {
  if (!hostCard?.equippedCards) return;
  const idx = hostCard.equippedCards.findIndex((c) => c.id === equippedId);
  if (idx === -1) return;
  // Engine-driven lock (Cursed Iron: "Artifacts here can't be
  // dropped"). The UI already gates the button; the second check
  // here protects against alternate call sites. Auth reads the
  // server-derived boardLegality bag (the server also rejects the
  // SET_EQUIPMENT drop); sandbox derives locally.
  const locked = lockedEquipmentIdSet(host);
  if (locked.has(equippedId)) {
    toast.error(t("Locked by an aura. Can't detach"));
    return;
  }
  const equipped = hostCard.equippedCards[idx];
  hostCard.equippedCards.splice(idx, 1);
  delete equipped._carrierId;

  // Clear any ledger entries the artifact was contributing while
  // equipped. AddCardToTable below re-spawns it as an inert table
  // card with no automation firing.
  host._clearEquippedArtifactEffects(equippedId);

  const hostMesh = host.meshes.get(hostCard.id);
  const pos = hostMesh?.position;
  if (pos) {
    // Small lateral nudge so the freshly-dropped card doesn't sit
    // exactly on top of the host. Stays visible as a separate
    // body that can be picked up immediately.
    const angle = Math.random() * Math.PI * 2;
    equipped.x = pos.x + Math.cos(angle) * 1.6;
    equipped.z = pos.z + Math.sin(angle) * 1.6;
  }
  // Re-spawn the equipment silently so the opponent doesn't get a
  // "played X to the field" log line. We broadcast a dedicated
  // "X dropped Y" log message right after so both sides see the
  // accurate wording. The card:place state change still flows so
  // the mesh appears on the opponent's table.
  const hostName = hostCard.name || msg('A card');
  const equipmentName = equipped.name || msg('a card');
  // A dropped artifact stays in whatever region the carrier was in:
  // dropped while the bearer is buried/submerged, it remains below the
  // surface (artifacts survive subsurface). Copy the carrier's region
  // before the re-spawn, which would otherwise default it to surface.
  equipped.subsurface = hostCard.subsurface ?? null;
  host.addCardToTable(equipped, true, { silent: true });
  host._broadcastCardSubsurface?.(equipped.id, equipped.subsurface);
  host._broadcastCardEquipment(hostCard.id, hostCard.equippedCards);
  host.logAction('{hostName} dropped {equipmentName}', { hostName, equipmentName });
  // Dropping a Lance clears its status badge.
  host.rebuildStatusBadges(hostCard.id);
  host.syncHoverStatusOverlay();
  host.syncEquipmentOverlays();
  // The token moves back to the re-spawned card (addCardToTable above);
  // clear it from the former bearer.
  host._refreshCounterHUD(hostCard.id);
}

/**
 * Drop every piece of equipment attached to `hostCard` onto the
 * table at the given position. Fired from removeCardFromTable so
 * leaving the field (sent to hand, banished, sent to cemetery,
 * sent to pile) detaches everything to the host's last spot, fanned
 * out so the cards don't pile invisibly on the same coordinates.
 */
export function detachAllEquipmentToField(host, hostCard, anchor, { broadcast = true } = {}) {
  const list = hostCard.equippedCards;
  if (!list || list.length === 0) return;
  // Clear ledger contributions for every artifact about to detach.
  // Has to happen before addCardToTable below. The re-spawned
  // artifacts shouldn't continue contributing once back on the table.
  for (const equipped of list) {
    host._clearEquippedArtifactEffects(equipped.id);
    delete equipped._carrierId;
  }
  hostCard.equippedCards = [];
  const hostName = hostCard.name || 'A card';
  list.forEach((equipped, i) => {
    const angle = (i / list.length) * Math.PI * 2;
    equipped.x = (anchor?.x ?? 0) + Math.cos(angle) * 1.8;
    equipped.z = (anchor?.z ?? 0) + Math.sin(angle) * 1.8;
    // Re-spawn each equipment card with `silent: true`. The
    // card:place state change still propagates so the opponent's
    // table picks up the new mesh, but neither side's log writes
    // "Played X." The accurate "X dropped Y" line is broadcast
    // separately below so both clients agree on the wording.
    // Keep the dropped artifact in the carrier's region (buried /
    // submerged carriers leave their artifacts below the surface).
    equipped.subsurface = hostCard.subsurface ?? null;
    // broadcast=false: a render-only removal (mirror fold / relay receive)
    // detaches locally without re-announcing. The other side runs the same
    // detach from its own copy of the event, so broadcasting from here
    // placed + logged every artifact once per client.
    host.addCardToTable(equipped, broadcast, { silent: true });
    if (broadcast) host._broadcastCardSubsurface?.(equipped.id, equipped.subsurface);
    const equipmentName = equipped.name || msg('a card');
    if (broadcast) host.logAction('{hostName} dropped {equipmentName}', { hostName, equipmentName });
    else host.addLogEntry(msg('{hostName} dropped {equipmentName}', { hostName, equipmentName }), 'system');
  });
  // Clear the host's equippedCards on the opponent's side. The
  // host's own card:remove broadcast (fired right after this in
  // removeCardFromTable) won't carry equipment info, so without
  // this the opponent's mirror of the host card would still hold
  // a stale equippedCards array. Relevant when the host card
  // returns to the table (sendToHand → re-play).
  if (broadcast) host._broadcastCardEquipment(hostCard.id, []);
  host.syncEquipmentOverlays();
}

/**
 * Detach a carried card (tagged with `_carrierId` by the scope resolver)
 * from its bearer and re-spawn it as a free board card at the bearer's
 * square, inheriting the bearer's region. Returns the now-free card so a
 * mutating effect can act on it as a normal on-table card; returns the
 * card untouched when it isn't carried.
 *
 * This is the bridge that lets any location effect (submerge/destroy/
 * damage everything here) act on equipped artifacts and carried units.
 * which have no own mesh while carried.
 */
export function materializeCarried(host, card) {
  const carrierId = card?._carrierId;
  if (!carrierId) return card;
  delete card._carrierId;
  const bearerMesh = host.meshes.get(carrierId);
  const bearer = bearerMesh?.userData?.cardInstance;
  const idx = bearer?.equippedCards?.findIndex((c) => c.id === card.id) ?? -1;
  if (idx === -1) return card; // already detached by an earlier effect
  bearer.equippedCards.splice(idx, 1);
  host._clearEquippedArtifactEffects(card.id);
  const pos = bearerMesh.position;
  const angle = Math.random() * Math.PI * 2;
  card.x = pos.x + Math.cos(angle) * 1.6;
  card.z = pos.z + Math.sin(angle) * 1.6;
  // Inherit the bearer's region so a carried card on a submerged bearer
  // re-spawns below the surface (same convention as detachEquipment).
  card.subsurface = bearer.subsurface ?? null;
  host.addCardToTable(card, true, { silent: true });
  host._broadcastCardSubsurface?.(card.id, card.subsurface);
  host._broadcastCardEquipment(bearer.id, bearer.equippedCards);
  host.logAction('{card} drops {equipment}', { card: cardLogLabel(bearer), equipment: cardLogLabel(card) });
  host.rebuildStatusBadges(bearer.id);
  host.syncEquipmentOverlays();
  return card;
}
